import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Selector from '../../components/forms/Selector';
import ParticipantSearchSelect from './ParticipantSearchSelect';
import WordSearchSelect from './WordSearchSelect';
import WordEditModal from './WordEditModal';
import IconSave from '../../components/icons/IconSave';
import IconDelete from '../../components/icons/IconDelete';
import IconAdd from '../../components/icons/IconAdd';
import IconGoogleCalendar from '../../components/icons/IconGoogleCalendar';
import IconCalendarDate from '../../components/icons/IconCalendarDate';
import IconClock from '../../components/icons/IconClock';
import IconCoins from '../../components/icons/IconCoins';
import IconGroups from '../../components/icons/IconGroups';
import IconTag from '../../components/icons/IconTag';
import IconChevronDown from '../../components/icons/IconChevronDown';
import IconClipboardList from '../../components/icons/IconClipboardList';
import IconFileText from '../../components/icons/IconFileText';
import IconHome from '../../components/icons/IconHome';
import IconPencilTool from '../../components/icons/IconPencilTool';
import IconCycled from '../../components/icons/IconCycled';
import IconStudent from '../../components/icons/IconSudent';
import IconSend from '../../components/icons/IconSend';
import LessonTopicFlyout from './LessonTopicFlyout';
import LessonHomeworkPanel from './LessonHomeworkPanel';
import LessonNegotiationHistoryPopup from './LessonNegotiationHistoryPopup';
import * as rescheduleApi from './rescheduleApi';
import wsManager from '../../services/wsManager';
import PhraseComposer from './PhraseComposer';
import phraseTypeLabel from './phraseTypeLabel';
import language from '../../variables/language';
import '../../css/modals.css';
import '../../css/lesson-detail-modern.css';
import '../../css/phrase-composer.css';
import '../../css/reschedule.css';

// Split by how often a teacher actually touches each field: date/start/
// end/price/group are the lesson's own identity, glanced at and edited
// constantly - rendered as the "hero" row. duration/repeat/teacher/bot-
// broadcast are set-once-and-forget - tucked behind the "more settings"
// toggle instead of competing for space with the hero row every time this
// modal opens.
const HERO_TIME_FIELDS = [
   {name:'start', type:'time'},
   {name:'end', type:'time'},
];
const HERO_DATE_FIELD = {name:'date', type:'date'};
const HERO_PRICE_FIELD = {name:'price', type:'number'};
const HERO_GROUP_SELECTOR = {name:'groupe', url:'/api/groups'};
const MORE_FIELDS = [
   {name:'duration', type:'number'},
];
const MORE_SHORT_SELECTORS = [
   {name:'isCycled', url:'/api/booleans'},
];
const MORE_WIDE_SELECTORS = [
   {name:'teacher', url:'/api/users'},
   {name:'hasBroadcast', url:'/api/booleans'},
];
const TEXT_AREAS = ['course', 'resources', 'homework', 'comment'];
const TEXT_AREA_ICONS = { course: IconClipboardList, resources: IconFileText, homework: IconHome, comment: IconPencilTool };
// the Lesson entity's field is still named `comment` (its old label/uses
// have nothing to do with this) - displayed as "Notes" since that's what it
// actually is here: a free-form notes box under the homework field, not a
// comment on anything.
const TEXT_AREA_LABEL_KEYS = { course: 'course', resources: 'resources', homework: 'homework', comment: 'notes' };
const ALL_FIELDS = [...HERO_TIME_FIELDS, HERO_DATE_FIELD, HERO_PRICE_FIELD, HERO_GROUP_SELECTOR, ...MORE_FIELDS, ...MORE_SHORT_SELECTORS, ...MORE_WIDE_SELECTORS];

const resolveValue = (val) => {
   if(val && typeof val === 'object' && val.id !== undefined) {
      return String(val.id);
   }
   if(val === null || val === undefined || val === 'null' || val === 'undefined') {
      return '';
   }
   return String(val);
}

const weekdayColorClass = (isoWeekday) => {
   if(isoWeekday === 6) return 'lesson-detail-weekday-saturday';
   if(isoWeekday === 7) return 'lesson-detail-weekday-sunday';
   return 'lesson-detail-weekday-workday';
}

// Luxon cascades a multi-unit diff so every unit but the last is a whole
// number - the last one keeps the fractional remainder, so it still needs
// flooring even though the bigger units technically don't.
const formatDuration = (diff, lang, withMonths) => {
   const months = withMonths ? Math.floor(diff.months || 0) : 0;
   const days = Math.floor(diff.days || 0);
   const hours = Math.floor(diff.hours || 0);
   const minutes = Math.floor(diff.minutes || 0);
   const parts = [];
   if(months > 0) parts.push(months + ' ' + lang.monthsShort);
   if(months > 0 || days > 0) parts.push(days + ' ' + lang.daysShort);
   if(months > 0 || days > 0 || hours > 0) parts.push(hours + ' ' + lang.hoursShort);
   parts.push(minutes + ' ' + lang.minutesShort);
   return parts.join(' ');
}

const getLessonSchedule = (formValues, lang) => {
   if(!formValues || !formValues.date) {
      return null;
   }
   const startDt = DateTime.fromFormat(formValues.date + ' ' + (formValues.start || '00:00'), 'yyyy-MM-dd HH:mm');
   if(!startDt.isValid) {
      return null;
   }
   const endDt = formValues.end
      ? DateTime.fromFormat(formValues.date + ' ' + formValues.end, 'yyyy-MM-dd HH:mm')
      : startDt;
   const now = DateTime.now();
   let statusText;
   if(now < startDt) {
      statusText = lang.lessonStartsIn + ' ' + formatDuration(startDt.diff(now, ['days', 'hours', 'minutes']), lang, false);
   } else if(now > endDt) {
      statusText = lang.lessonEndedLabel + ' ' + formatDuration(now.diff(endDt, ['months', 'days', 'hours', 'minutes']), lang, true) + ' ' + lang.agoSuffix;
   } else {
      statusText = lang.lessonInProgress;
   }
   return {
      weekdayLabel: lang.weekDaysFull[startDt.weekday - 1],
      weekdayClass: weekdayColorClass(startDt.weekday),
      statusText,
   };
}

const flattenLesson = (data) => {
   if(!data || !data.id) {
      return null;
   }
   const flat = {};
   ALL_FIELDS.forEach(f => flat[f.name] = resolveValue(data[f.name]));
   TEXT_AREAS.forEach(name => flat[name] = resolveValue(data[name]));
   return flat;
}

export default function LessonDetailModal({ isOpen, lessonId, onClose, onSaved, onParticipantsChanged }) {
   const navigate = useNavigate();
   const [formValues, setFormValues] = useState(null);
   const [initialValues, setInitialValues] = useState(null);
   const [calendarLink, setCalendarLink] = useState('');
   const [saving, setSaving] = useState(false);
   const [historyPopupOpen, setHistoryPopupOpen] = useState(false);
   const [negotiationSummary, setNegotiationSummary] = useState({ hasAny: false, wasAccepted: false, wasApprovedCancel: false });
   const [participants, setParticipants] = useState([]);
   const [newStudentId, setNewStudentId] = useState('');
   const [addingParticipant, setAddingParticipant] = useState(false);
   const [togglingKey, setTogglingKey] = useState(null);
   const [savingToggle, setSavingToggle] = useState(false);
   const [lessonWords, setLessonWords] = useState([]);
   const [pickedLessonWordId, setPickedLessonWordId] = useState('');
   const [addingWord, setAddingWord] = useState(false);
   const [lessonWordQuery, setLessonWordQuery] = useState('');
   const [lessonWordNoMatch, setLessonWordNoMatch] = useState(false);
   const [lessonWordResetKey, setLessonWordResetKey] = useState(0);
   const [quickAddWordOpen, setQuickAddWordOpen] = useState(false);
   const [lessonPhrases, setLessonPhrases] = useState([]);
   const [phraseTypes, setPhraseTypes] = useState([]);
   const [activePhraseTypeId, setActivePhraseTypeId] = useState(null);
   const [showMore, setShowMore] = useState(false);

   const loadLesson = () => {
      axios.get('/api/lessons/' + lessonId, { withCredentials: true })
         .then(response => {
            const flat = flattenLesson(response.data);
            if(flat) {
               setFormValues(flat);
               setInitialValues(flat);
               setCalendarLink(response.data.googleEventHtmlLink || '');
            }
         })
         .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
   }
   const loadParticipants = () => {
      axios.get('/api/events?lesson=' + lessonId, { withCredentials: true })
         .then(response => setParticipants(Array.isArray(response.data) ? response.data : []))
         .catch(() => setParticipants([]));
   }
   const loadLessonWords = () => {
      axios.get('/api/word-lessons', { params: { lessonId }, withCredentials: true })
         .then(response => setLessonWords(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLessonWords([]));
   }
   const loadLessonPhrases = () => {
      axios.get('/api/phrase-lessons', { params: { lessonId }, withCredentials: true })
         .then(response => setLessonPhrases(Array.isArray(response.data) ? response.data : []))
         .catch(() => setLessonPhrases([]));
   }

   // Phrase/Sentence (and whatever gets added later) tabs - the list itself
   // drives how many tabs render, so a 3rd type added later via
   // POST /api/phrase-types shows up here automatically, no code change.
   useEffect(() => {
      axios.get('/api/phrase-types', { withCredentials: true })
         .then(response => {
            const types = Array.isArray(response.data) ? response.data : [];
            setPhraseTypes(types);
            setActivePhraseTypeId(prev => prev || (types.length > 0 ? types[0].id : null));
         })
         .catch(() => {});
   }, []);

   useEffect(() => {
      if(!isOpen || !lessonId) {
         setFormValues(null);
         setInitialValues(null);
         setCalendarLink('');
         return;
      }
      loadLesson();
      loadParticipants();
      loadLessonWords();
      loadLessonPhrases();
      loadNegotiationSummary();
      setNewStudentId('');
      setPickedLessonWordId('');
      setShowMore(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, lessonId]);

   // At-a-glance "was this lesson ever rescheduled/cancelled" signal - the
   // history button alone gave no indication anything was there to look at,
   // and a resolved (ACCEPTED/APPROVED) request otherwise vanishes from every
   // "pending" list with nothing left pointing back at it. badgeText is only
   // set for a resolved outcome; hasAny alone (e.g. a declined attempt) just
   // highlights the button so it's still discoverable via a click.
   //
   // "Скасовано" needs BOTH signals, not just one - a teacher's direct
   // no-show toggle has no cancel-request at all (attendence-only path), but
   // a LATE-approved cancel request (< 4h notice) deliberately does NOT set
   // attendence=2 either (bills as attended, by design) - attendence alone
   // would miss that real case. Same fix just made on the platform side for
   // its `attendanceCancelled` field, after a real report of exactly this
   // (a late-cancelled lesson showing no badge anywhere on the student side).
   const loadNegotiationSummary = () => {
      if(!lessonId) { setNegotiationSummary((prev) => ({ ...prev, hasAny: false, wasAccepted: false, wasApprovedCancel: false })); return; }
      Promise.all([
         rescheduleApi.getLessonRescheduleRequests(lessonId).catch(() => ({ data: [] })),
         rescheduleApi.getLessonCancelRequests(lessonId).catch(() => ({ data: [] })),
      ]).then(([rescheduleRes, cancelRes]) => {
         const reschedules = Array.isArray(rescheduleRes.data) ? rescheduleRes.data : [];
         const cancels = Array.isArray(cancelRes.data) ? cancelRes.data : [];
         setNegotiationSummary((prev) => ({
            ...prev,
            hasAny: reschedules.length > 0 || cancels.length > 0,
            wasAccepted: reschedules.some(r => r.status === 'ACCEPTED'),
            wasApprovedCancel: cancels.some(c => c.status === 'APPROVED'),
         }));
      });
   };

   // Live refresh of the same summary while the modal stays open - mirrors
   // RescheduleResponseModal.jsx's own subscription (same topic, same
   // "just refetch, don't reconcile the payload" approach).
   useEffect(() => {
      if(!isOpen || !lessonId) return;
      const topic = '/topic/reschedule/' + lessonId;
      const handler = () => loadNegotiationSummary();
      wsManager.subscribe(topic, handler);
      return () => wsManager.unsubscribe(topic, handler);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, lessonId]);

   const isDirty = useMemo(() => {
      if(!formValues || !initialValues) {
         return false;
      }
      return JSON.stringify(formValues) !== JSON.stringify(initialValues);
   }, [formValues, initialValues]);

   // Autosave: no Save button - every field change (hero row, more-settings,
   // textareas alike) persists on its own, debounced. `initialValues` is set
   // to the exact snapshot that was actually PUT (captured at call time), not
   // re-fetched from the server - a re-fetch would race a user who kept
   // typing during the request and clobber their newest keystrokes with the
   // pre-save server response. Setting it to the sent snapshot instead is
   // race-free: if formValues moved on again in the meantime, isDirty simply
   // stays true against that snapshot and the effect below fires again.
   const handleSave = () => {
      if(!isDirty || saving || !formValues) {
         return;
      }
      setSaving(true);
      const snapshot = formValues;
      const payload = {};
      Object.keys(snapshot).forEach(key => {
         if(snapshot[key] !== '') payload[key] = snapshot[key];
      });
      axios.put('/api/lessons/' + lessonId, JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         // NOT onSaved() here - at least one caller's onSaved (Events.jsx's
         // onRefreshHundler) also closes the modal, which made sense for the
         // old single-click "Save" button but would now close it after the
         // very first autosaved keystroke pause. onSaved() is deferred to
         // handleClose() below instead, firing once when the modal actually
         // goes away, exactly where "refresh the parent's view now" belongs.
         setInitialValues(snapshot);
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => {
         setSaving(false);
      });
   }

   // Debounced trigger for the autosave above - resets on every keystroke/
   // change (the effect cleanup clears the pending timeout), so a save only
   // actually fires 700ms after the user stops touching the form. isDirty
   // becomes false the instant handleSave succeeds (initialValues catches up
   // to the sent snapshot), so this naturally stops re-arming once nothing is
   // left to save - not an infinite loop.
   useEffect(() => {
      if(!isDirty || saving) {
         return;
      }
      const handle = setTimeout(() => {
         handleSave();
      }, 700);
      return () => clearTimeout(handle);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [formValues, isDirty, saving]);

   if(!isOpen) return '';

   const stopProp = e => e.stopPropagation();
   const lang = language[language.lang];
   const schedule = getLessonSchedule(formValues, lang);
   // Same source of truth as the platform's own (corrected) `attendanceCancelled`
   // field: attendence==2 (a teacher's direct no-show toggle, or an on-time
   // approved cancellation) OR an APPROVED cancel request exists (also
   // catches a LATE-approved one, which deliberately leaves attendence
   // untouched so it still bills as attended).
   const attendanceCancelled = participants.some(p => Number(p.attendence) === 2) || negotiationSummary.wasApprovedCancel;
   // Cancellation wins over an earlier accepted reschedule - a lesson that
   // got rescheduled and THEN cancelled is, in the end, not happening
   // (real case: a lesson accepted a reschedule in an earlier test round,
   // then a later cancel request got approved - showing "Перенесено" there
   // would be actively misleading).
   const negotiationBadgeText = attendanceCancelled ? 'Скасовано' : negotiationSummary.wasAccepted ? 'Перенесено' : '';

   const handleFieldChange = (name, value) => {
      setFormValues(prev => ({...prev, [name]: value}));
   }
   // ::-webkit-calendar-picker-indicator is hidden via display:none below
   // (own icon already leads the field) - but Chromium ties its native
   // "click anywhere in the field opens the calendar" behavior to that
   // indicator existing, so hiding it also silently kills the popup for
   // the whole input. showPicker() bypasses that and reopens it explicitly.
   const openPicker = (e) => {
      if (e.target.showPicker) e.target.showPicker();
   }

   // The one place that flushes onSaved() to the parent now that fields
   // autosave silently in the background - every "this modal is going away"
   // exit uses this instead of a raw onClose(), so the parent's own list
   // (price/date/etc. shown elsewhere) picks up whatever autosaved while it
   // was open, exactly once, at the moment it'd actually be visible again.
   const handleClose = () => {
      onSaved();
      onClose();
   }

   const handleDeleteLesson = () => {
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) return;
      axios.delete('/api/lessons/' + lessonId, { withCredentials: true })
         .then(handleClose)
         .catch(err => console.log(err));
   }

   const handleAddParticipant = () => {
      if(!newStudentId || addingParticipant) return;
      setAddingParticipant(true);
      axios.post('/api/events', JSON.stringify({ lesson: String(lessonId), student: newStudentId }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setNewStudentId('');
         loadParticipants();
         if(onParticipantsChanged) onParticipantsChanged();
      }).catch(err => console.log(err))
        .finally(() => setAddingParticipant(false));
   }

   const handleRemoveParticipant = (eventId) => {
      axios.delete('/api/events/' + eventId, { withCredentials: true })
         .then(() => {
            loadParticipants();
            if(onParticipantsChanged) onParticipantsChanged();
         })
         .catch(err => console.log(err));
   }

   // Words attach directly to the Lesson (shared across every participant) -
   // one shared list read/edited from here. On top of that shared record,
   // this also seeds a personal words_student entry for every current
   // participant (proficiency defaults to 1 server-side and
   // WordStudentService.findOrCreate is idempotent, so it's safe to fire
   // even if a student already has the word) - a word taught in a lesson
   // should land in each student's own vocabulary bank without the teacher
   // repeating the same pick on every student's own card. Removal stays
   // lesson-only on purpose - unlinking a word from a lesson's plan doesn't
   // mean a student should lose it from their personal vocabulary. Shared by
   // both add paths: picking an existing word (handleAddLessonWord) and
   // quick-creating a brand new one via the "+AI" button (handleQuickWordSaved).
   const attachWordToLesson = (wordId) => {
      return axios.post('/api/word-lessons', JSON.stringify({ lesson: lessonId, word: wordId }), {
         withCredentials: true, headers: {'Content-Type': 'application/json'},
      }).then(() => {
         const studentIds = participants.filter(ev => ev.student && ev.student.id).map(ev => ev.student.id);
         return Promise.all(studentIds.map(studentId =>
            axios.post('/api/wordstudents', JSON.stringify({ student: studentId, word: wordId }), {
               withCredentials: true, headers: {'Content-Type': 'application/json'},
            }).catch(() => {})
         ));
      });
   }

   const handleAddLessonWord = () => {
      if(!pickedLessonWordId || addingWord) return;
      setAddingWord(true);
      attachWordToLesson(pickedLessonWordId).then(() => {
         setPickedLessonWordId('');
         setLessonWordResetKey(prev => prev + 1);
         loadLessonWords();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setAddingWord(false));
   }

   // "no word in dictionary" quick-add: opens the same create form as the
   // Слова tab (WordEditModal), pre-filled with what was typed. Once that
   // form saves the brand new Word, it's wired into this lesson (and cascaded
   // to participants) exactly like an existing word picked from the search.
   const openQuickAddWord = () => setQuickAddWordOpen(true);
   const closeQuickAddWord = () => setQuickAddWordOpen(false);
   const handleQuickWordSaved = (newWord) => {
      closeQuickAddWord();
      if(!newWord || !newWord.id) {
         return;
      }
      attachWordToLesson(newWord.id).then(() => {
         setPickedLessonWordId('');
         setLessonWordResetKey(prev => prev + 1);
         loadLessonWords();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      });
   }

   const handleRemoveLessonWord = (wordLessonId) => {
      axios.delete('/api/word-lessons/' + wordLessonId, { withCredentials: true })
         .then(() => loadLessonWords())
         .catch(err => console.log(err));
   }

   const handleRemoveLessonPhrase = (phraseLessonId) => {
      axios.delete('/api/phrase-lessons/' + phraseLessonId, { withCredentials: true })
         .then(() => loadLessonPhrases())
         .catch(err => console.log(err));
   }

   const toggleKey = (eventId, field) => eventId + '-' + field;

   const handleBadgeClick = (eventId, field) => {
      if(savingToggle) return;
      const key = toggleKey(eventId, field);
      setTogglingKey(prev => prev === key ? null : key);
   }

   const handleToggleConfirm = (eventId, field, targetStatus) => {
      if(savingToggle) return;
      // the backend can't tell "0 because unset" from "0 because this key
      // wasn't sent" (both hit a primitive long's default) - -1 is a
      // sentinel it reads as "explicitly clear back to unset".
      const newValue = targetStatus === 0 ? -1 : targetStatus;
      setSavingToggle(true);
      axios.put('/api/events/' + eventId, JSON.stringify({ [field]: newValue }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setTogglingKey(null);
         loadParticipants();
         if(onParticipantsChanged) onParticipantsChanged();
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => setSavingToggle(false));
   }

   const goToStudent = (id) => {
      handleClose();
      navigate('/students/' + id);
   }

   // click a badge to open a tiny picker right in its place; clicking one of
   // the other options saves immediately via PUT and updates the indicator.
   // attendence supports a genuine "not set" state (never hidden here, unlike
   // the read-only events table, since this is the only place to reach it).
   // toPay can NOT be left unset - Sum price/hours math treats an unset
   // toPay the same as an explicit "Yes" (see EventService.getInfoparticlesByEvents),
   // so it only ever toggles between Yes/No and unset defaults to displaying "Yes".
   const statusLabel = (status) => status === 1 ? lang.Yes : (status === 2 ? lang.No : lang['not selected']);
   const statusClass = (status) => status === 1 ? 'on' : (status === 2 ? 'off-explicit' : 'unset');

   const renderToggleBadge = (ev, field, fieldLabel, allowUnset) => {
      const raw = ev[field];
      const current = allowUnset
         ? ((raw === 1 || raw === 2) ? raw : 0)
         : (raw === 2 ? 2 : 1);
      const isOpen = togglingKey === toggleKey(ev.id, field);
      if(isOpen) {
         const otherStatuses = (allowUnset ? [1, 2, 0] : [1, 2]).filter(s => s !== current);
         return (
            <span key={field} className='lesson-detail-status-picker'>
               <span className='lesson-detail-status-picker-label'>{fieldLabel}:</span>
               {otherStatuses.map(s => (
                  <span key={s} className='student-lessons-badge status-option'
                     onClick={() => handleToggleConfirm(ev.id, field, s)}>
                     {statusLabel(s)}
                  </span>
               ))}
            </span>
         );
      }
      return (
         <span key={field} className={'student-lessons-badge toggle-clickable ' + statusClass(current)}
            onClick={() => handleBadgeClick(ev.id, field)}>
            <i className='student-lessons-badge-dot'></i>{fieldLabel}: {statusLabel(current)}
         </span>
      );
   }

   return (
      <>
      <div className="modal-menu" onClick={handleClose} style={{ display: 'flex' }}>
         <div className="modal-window lesson-detail-window" onClick={stopProp}>
            <div className='lesson-detail-header'>
               <div className='lesson-detail-header-left'>
                  <h3 className="modal-w-title lesson-detail-title">{lang.modals.lessons.updateTitle}</h3>
                  {calendarLink ? (
                     <a className='lesson-detail-calendar-link' href={calendarLink} target='_blank' rel='noopener noreferrer'
                        onClick={e => e.stopPropagation()} title='Google Calendar'>
                        <IconGoogleCalendar fillColor='icon-red' style={{width:16, height:16}} />
                     </a>
                  ) : ''}
                  <LessonTopicFlyout lessonId={lessonId} participants={participants} onClose={handleClose} />
               </div>
               {schedule ? (
                  <div className='lesson-detail-schedule-info'>
                     <span className={'lesson-detail-weekday ' + schedule.weekdayClass}>{schedule.weekdayLabel}</span>
                     <span className='lesson-detail-schedule-status'>{schedule.statusText}</span>
                  </div>
               ) : ''}
               {negotiationBadgeText ? (
                  <span className='lesson-detail-negotiation-badge' onClick={() => setHistoryPopupOpen(true)}>{negotiationBadgeText}</span>
               ) : ''}
               <div className={'lesson-detail-history-btn' + ((negotiationSummary.hasAny || attendanceCancelled) ? ' has-history' : '')}
                  onClick={() => setHistoryPopupOpen(true)} title='Історія перенесень і скасувань'>
                  <IconClock style={{width:14, height:14, margin:0}} />
               </div>
               <div className='lesson-detail-delete-btn' onClick={handleDeleteLesson}>
                  <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
               </div>
            </div>

            {!formValues ? (
               <div className='event-edit-loading'>{lang['Loading ...']}</div>
            ) : (
               <div className='lesson-detail-body'>
                  <div className='lesson-detail-form-col'>
                     <div className='lesson-hero'>
                        <div className='lesson-hero-field'>
                           <span className='lesson-hero-icon'><IconCalendarDate /></span>
                           <input className='lesson-hero-input lesson-hero-date' type='date' onClick={openPicker}
                              value={formValues.date} onChange={(e) => handleFieldChange('date', e.target.value)} />
                        </div>
                        <div className='lesson-hero-field'>
                           <span className='lesson-hero-icon'><IconClock style={{ fill: 'var(--lighttext-color)', width: 16, height: 16 }} /></span>
                           <input className='lesson-hero-input lesson-hero-time' type='time' onClick={openPicker}
                              value={formValues.start} onChange={(e) => handleFieldChange('start', e.target.value)} />
                           <span className='lesson-hero-sep'>–</span>
                           <input className='lesson-hero-input lesson-hero-time' type='time' onClick={openPicker}
                              value={formValues.end} onChange={(e) => handleFieldChange('end', e.target.value)} />
                        </div>
                        <div className='lesson-hero-field'>
                           <span className='lesson-hero-icon'><IconCoins /></span>
                           <input className='lesson-hero-input lesson-hero-price' type='number'
                              value={formValues.price} onChange={(e) => handleFieldChange('price', e.target.value)} />
                           <span className='lesson-hero-currency'>zł</span>
                        </div>
                        <div className='lesson-hero-field'>
                           <span className='lesson-hero-icon'><IconGroups /></span>
                           <Selector className='lesson-hero-select' name='groupe' url={HERO_GROUP_SELECTOR.url}
                              value={formValues.groupe} disabled={false}
                              selectorsHundler={(value) => handleFieldChange('groupe', value)} />
                        </div>
                     </div>

                     <div className={'lesson-more-toggle' + (showMore ? ' open' : '')} onClick={() => setShowMore(v => !v)}>
                        <IconChevronDown />
                        {showMore ? lang.lessonHideMore : lang.lessonShowMore}
                     </div>
                     <div className={'lesson-more-panel' + (showMore ? ' open' : '')}>
                        <div className='lesson-more-grid'>
                           <div className='lesson-more-field'>
                              <span className='lesson-content-label'><IconClock style={{ fill: 'var(--lighttext-color)', width: 14, height: 14 }} />{lang.duration}</span>
                              <div className='lesson-hero-field'>
                                 <input className='lesson-hero-input' type='number'
                                    value={formValues.duration} onChange={(e) => handleFieldChange('duration', e.target.value)} />
                                 <span className='lesson-hero-currency'>{lang.minutesShort}</span>
                              </div>
                           </div>
                           <div className='lesson-more-field'>
                              <span className='lesson-content-label'><IconCycled style={{ fill: 'var(--lighttext-color)', width: 14, height: 14 }} />{lang.isCycled}</span>
                              <div className='lesson-hero-field'>
                                 <Selector className='lesson-hero-select' name='isCycled' url='/api/booleans'
                                    value={formValues.isCycled} disabled={false}
                                    selectorsHundler={(value) => handleFieldChange('isCycled', value)} />
                              </div>
                           </div>
                           <div className='lesson-more-field'>
                              <span className='lesson-content-label'><IconStudent style={{ fill: 'var(--lighttext-color)', width: 14, height: 14 }} />{lang.teacher}</span>
                              <div className='lesson-hero-field'>
                                 <Selector className='lesson-hero-select' name='teacher' url='/api/users'
                                    value={formValues.teacher} disabled={false}
                                    selectorsHundler={(value) => handleFieldChange('teacher', value)} />
                              </div>
                           </div>
                           <div className='lesson-more-field'>
                              <span className='lesson-content-label'><IconSend style={{ width: 14, height: 14 }} />{lang.hasBroadcast}</span>
                              <div className='lesson-hero-field'>
                                 <Selector className='lesson-hero-select' name='hasBroadcast' url='/api/booleans'
                                    value={formValues.hasBroadcast} disabled={false}
                                    selectorsHundler={(value) => handleFieldChange('hasBroadcast', value)} />
                              </div>
                           </div>
                        </div>
                     </div>

                     <div className='lesson-content-section'>
                        {TEXT_AREAS.map(name => {
                           const Icon = TEXT_AREA_ICONS[name];
                           const label = lang[TEXT_AREA_LABEL_KEYS[name]];
                           return (
                              <div className='lesson-content-field' key={name}>
                                 <span className='lesson-content-label'><Icon />{label}</span>
                                 <textarea className='lesson-content-textarea'
                                    placeholder={label}
                                    value={formValues[name]}
                                    onChange={(e) => handleFieldChange(name, e.target.value)} />
                              </div>
                           );
                        })}
                     </div>

                     {saving ? (
                        <div className='lesson-detail-autosave-status'>
                           <IconSave />
                           {lang.saving}
                        </div>
                     ) : ''}
                  </div>

                  <div className='lesson-detail-participants-col'>
                     <div className='lesson-detail-subsection'>
                        <p className='student-card-section-title'>
                           <span className='lesson-detail-section-title-icon'><IconGroups /></span>
                           {lang.lessonParticipants}
                        </p>
                        <div className='lesson-detail-participants-list'>
                           {participants.map(ev => (
                              <div className='lesson-detail-participant-row' key={ev.id}>
                                 <div className='lesson-detail-participant-top'>
                                    <span className='lesson-detail-participant-name' onClick={() => goToStudent(ev.student && ev.student.id)}>
                                       {ev.student ? ev.student.name + ' ' + ev.student.surname : ''}
                                    </span>
                                    <div className='student-lessons-icon-btn' onClick={() => handleRemoveParticipant(ev.id)}>
                                       <IconDelete style={{fill:'#e0876c', width:13, height:13, margin:0}} />
                                    </div>
                                 </div>
                                 <div className='lesson-detail-participant-badges'>
                                    {renderToggleBadge(ev, 'toPay', lang.toPay, false)}
                                    {renderToggleBadge(ev, 'attendence', lang.attendence, true)}
                                 </div>
                              </div>
                           ))}
                           {participants.length === 0 ? <p className='lesson-detail-empty'>{lang.groupCard.noMembers}</p> : ''}
                        </div>
                        <div className='lesson-detail-add-row'>
                           <ParticipantSearchSelect value={newStudentId} onChange={setNewStudentId}
                              excludeIds={participants.map(ev => ev.student && ev.student.id)}
                              placeholder={lang.groupCard.addStudentButton} />
                           <div className='student-lessons-add-btn' onClick={handleAddParticipant} title={lang.groupCard.addStudentButton}>
                              <IconAdd />
                           </div>
                        </div>
                     </div>

                     <div className='lesson-detail-subsection'>
                        <p className='student-card-section-title'>
                           <span className='lesson-detail-section-title-icon'><IconTag /></span>
                           {lang.lessonWords}
                        </p>
                        <div className='lesson-detail-words-list'>
                           {lessonWords.map(wl => (
                              <span className='lesson-detail-word-chip' key={wl.id}>
                                 {wl.word ? wl.word.word : ''}
                                 <span className='lesson-detail-word-chip-remove' onClick={() => handleRemoveLessonWord(wl.id)}>×</span>
                              </span>
                           ))}
                           {lessonWords.length === 0 ? <span className='lesson-detail-words-empty'>{lang.lessonWordsEmpty}</span> : ''}
                        </div>
                        <div className='lesson-detail-add-row'>
                           <WordSearchSelect key={lessonWordResetKey} value={pickedLessonWordId} onChange={setPickedLessonWordId}
                              placeholder={lang.lessonWordsPlaceholder}
                              onNoMatchChange={(noMatch, query) => { setLessonWordNoMatch(noMatch); setLessonWordQuery(query); }} />
                           {lessonWordNoMatch ? (
                              <div className='student-lessons-add-btn lesson-word-quick-ai-btn' onClick={openQuickAddWord} title={lang.lessonWordsAddNewBtn}>
                                 <IconAdd /><span className='lesson-word-quick-ai-label'>AI</span>
                              </div>
                           ) : (
                              <div className={'student-lessons-add-btn' + (!pickedLessonWordId || addingWord ? ' conv-panel-add-btn-disabled' : '')}
                                 onClick={handleAddLessonWord} title={lang.lessonWordsAddBtn}>
                                 <IconAdd />
                              </div>
                           )}
                        </div>
                     </div>

                     <div className='lesson-detail-subsection'>
                        <p className='student-card-section-title'>
                           <span className='lesson-detail-section-title-icon'><IconTag /></span>
                           {lang.lessonPhrases}
                        </p>
                        {phraseTypes.length > 1 ? (
                           <div className='phrase-type-tabs'>
                              {phraseTypes.map(t => (
                                 <div key={t.id}
                                    className={'phrase-type-tab' + (activePhraseTypeId === t.id ? ' active' : '')}
                                    onClick={() => setActivePhraseTypeId(t.id)}>
                                    {phraseTypeLabel(t, lang.phrasesTab)}
                                 </div>
                              ))}
                           </div>
                        ) : ''}
                        <div className='phrase-chip-list'>
                           {lessonPhrases.filter(pl => pl.phrase && pl.phrase.type && pl.phrase.type.id === activePhraseTypeId).map(pl => (
                              <span className='phrase-chip' key={pl.id}>
                                 <span className='phrase-chip-text'>
                                    <span className='phrase-chip-sentence'>{pl.phrase ? pl.phrase.text : ''}</span>
                                    {pl.phrase && pl.phrase.translation ? <span className='phrase-chip-translation'>{pl.phrase.translation}</span> : ''}
                                    {pl.phrase && pl.phrase.tags && pl.phrase.tags.length > 0 ? (
                                       <span className='phrase-chip-tags'>
                                          {pl.phrase.tags.map(t => <span className='phrase-chip-tag' key={t.id}>{t.name}</span>)}
                                       </span>
                                    ) : ''}
                                 </span>
                                 <span className='phrase-chip-remove' onClick={() => handleRemoveLessonPhrase(pl.id)}>×</span>
                              </span>
                           ))}
                           {lessonPhrases.filter(pl => pl.phrase && pl.phrase.type && pl.phrase.type.id === activePhraseTypeId).length === 0 ? (
                              <span className='lesson-detail-words-empty'>{lang.lessonPhrasesEmpty}</span>
                           ) : ''}
                        </div>
                        <PhraseComposer lessonId={lessonId} typeId={activePhraseTypeId} onAttached={loadLessonPhrases} />
                     </div>

                     <div className='lesson-detail-subsection'>
                        <LessonHomeworkPanel lessonId={lessonId} onClose={handleClose}
                           lessonDate={formValues ? formValues.date : null} lessonStart={formValues ? formValues.start : null} />
                     </div>
                  </div>
               </div>
            )}
         </div>
      </div>
      <WordEditModal isOpen={quickAddWordOpen} wordId={null} studentId={undefined}
         initialWordText={lessonWordQuery} onClose={closeQuickAddWord} onSaved={handleQuickWordSaved} />
      <LessonNegotiationHistoryPopup isOpen={historyPopupOpen} lessonId={lessonId} onClose={() => setHistoryPopupOpen(false)} />
      </>
   );
}
